// Dataset type helpers: schema merging and matching datasets to a type.

/**
 * Merge a type's schema into a dataset's schema, adding the type's **missing** fields while
 * keeping the dataset's existing ones (a dataset may have customised its copy).
 *
 * - `properties`: the dataset's definitions win on conflict; the type's missing ones are added.
 * - `required`: union.
 * - other keywords: the dataset's win.
 *
 * @param {object|null} base The dataset's current schema.
 * @param {object|null} incoming The type's schema.
 */
export const mergeMetadataSchemas = (base, incoming) => {
  if (!base) return incoming ?? null;
  if (!incoming) return base;

  const baseProperties = base.properties && typeof base.properties === "object" ? base.properties : {};
  const incomingProperties = incoming.properties && typeof incoming.properties === "object" ? incoming.properties : {};

  const merged = { ...incoming, ...base };
  merged.properties = { ...incomingProperties, ...baseProperties };

  const required = [...new Set([...(base.required ?? []), ...(incoming.required ?? [])])];

  if (required.length > 0) {
    merged.required = required;
  } else {
    delete merged.required;
  }

  return merged;
};

/** Whether a dataset belongs to a type (by numeric id, public id, or the stored type name). */
export const datasetUsesType = (dataset, type) => {
  if (!dataset || !type) return false;

  const datasetIds = [dataset.dataset_type_id, dataset.dataset_type?.id, dataset.dataset_type?.public_id]
    .filter((value) => value !== undefined && value !== null)
    .map(String);

  const typeIds = [type.id, type.public_id]
    .filter((value) => value !== undefined && value !== null)
    .map(String);

  if (datasetIds.some((id) => typeIds.includes(id))) return true;

  return Boolean(type.name) && dataset.type === type.name;
};

/** The fields the type's schema defines. */
export const schemaFieldNames = (schema) => {
  if (!schema || typeof schema !== "object") return [];
  const properties = schema.properties && typeof schema.properties === "object" ? schema.properties : {};
  return Object.keys(properties);
};

export default mergeMetadataSchemas;
